<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { adService } from "@/app/services/AdService";
import { useCustomRouter } from "@/utils/router";
import { DisplaySlot } from "@/types/ad/DisplaySlot";
import { openInNewWindow } from "@/utils/browserUtil";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";

const props = defineProps({
  adSlot: {
    type: String,
    required: true
  }
});

const { pushIgnoreError } = useCustomRouter();
const ad = ref<DisplaySlot | null>(null);
const modalVisible = ref(false);

const containerId = computed(() => `ad-container-${ad.value?.slotName}`);

const handleAdClickEvent = (event: Event) => {
  if (ad.value) {
    adService.handleAdClickEvent(event, ad.value.id, ad.value.slotId);
  }
};

onMounted(async () => {
  await displaySlot();
});

onUnmounted(() => {
  window.removeEventListener("click", handleAdClickEvent);
});

watch(
  () => props.adSlot,
  async () => {
    await displaySlot();
  }
);

async function displaySlot() {
  const displaySlot = await adService.displaySlot(props.adSlot);
  if (displaySlot) {
    ad.value = displaySlot;

    // モーダル広告スロットの場合はモーダル表示フラグをたてる
    if (displaySlot.slotIsModal) {
      modalVisible.value = true;

      // 重複して登録されないようにイベントリスナーを削除してから登録
      window.removeEventListener("click", handleAdClickEvent);
      window.addEventListener("click", handleAdClickEvent);
    }

    // 表示イベントを送信（await しない）
    adService.registerEvent(displaySlot.id, "view_slot", displaySlot.slotId);
  }
}

function navigateToLink() {
  const adValue = ad.value!;
  const linkType = adValue.linkType;

  // クリックイベントを送信（await しない）
  // do_nothing でもイベントを送信する
  adService.registerEvent(
    adValue.id,
    "click_slot",
    adValue.slotId,
    JSON.stringify({
      linkType: linkType,
      redirectUrl: adValue.redirectUrl
    })
  );

  if (adValue.linkType === "internal") {
    if (adValue.redirectUrl) {
      pushIgnoreError(adValue.redirectUrl);
    }
  } else if (linkType === "external") {
    if (adValue.redirectUrl && adValue.redirectUrl.startsWith("https://")) {
      openInNewWindow(adValue.redirectUrl);
    }
  } else if (linkType === "detail_page") {
    pushIgnoreError({ name: "AdsDetail", params: { adId: adValue.id } });
  }
}

function onClickCloseModal() {
  modalVisible.value = false;
  adService.registerClickEvent(ad.value!.id, "close-modal", ad.value!.slotId);
}
</script>

<template>
  <div v-if="ad" :id="containerId">
    <div
      v-if="!ad.slotIsModal"
      data-testid="slot-html"
      v-html="ad.html"
      @click="navigateToLink"
    ></div>
    <BModal
      v-else
      v-model="modalVisible"
      hide-header
      hide-footer
      data-testid="modal-slot-html"
      id="ad-modal"
      body-class="ad-modal-body"
      centered
    >
      <FeatherIcon
        icon="x-square"
        size="32"
        class="close-icon"
        @click="onClickCloseModal"
      />

      <div v-html="ad.html"></div>
    </BModal>
  </div>
</template>

<style lang="scss">
// note: BModal 内部は バグ？により deep selector が機能しない
// bootstrap-vue 自体もアップデートが滞っているため、修正が難しい (ver 2.23.1)
// そのため、id を指定し global でスタイルを上書きしている
#ad-modal {
  .ad-modal-body {
    position: relative;
    padding: 0;
  }
}
</style>

<style lang="scss" scoped>
.close-icon {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 9999;
  margin: 12px;
}
</style>
